import React, { useEffect, useState } from 'react';
import { curatedModels } from '../data/models';
import { countryNames } from '../data/geography';
import { COMPILED_LABEL } from '../data/sources';
import { formatReleased } from '../utils/format';
import { getOpenRouterModels, OpenRouterResult } from '../utils/api';
import { modelCounts } from '../utils/ecosystem';
import { AccessFilter, ACCESS_NOTES, ACCESS_OPTIONS } from '../utils/modelFilters';
import { PageHeader } from '../components/PageHeader';
import { SourceRef } from '../components/SourceRef';
import { DataBadge } from '../components/DataBadge';
import { ChipGroup } from '../components/ChipGroup';
import { CuratedModelTable } from '../components/models/CuratedModelTable';
import { OpenRouterTable } from '../components/models/OpenRouterTable';
import { HFLiveTable } from '../components/models/HFLiveTable';
import { ModelMakers } from '../components/models/ModelMakers';
export function Models() {
  const total = curatedModels.length;
  const openShare = curatedModels.filter(m => m.license !== 'proprietary').length / total * 100;
  const newest = curatedModels.reduce<string | null>((acc, m) => m.released && (!acc || m.released > acc) ? m.released : acc, null);
  const [access, setAccess] = useState<AccessFilter>('all');
  const [country, setCountry] = useState('all');
  const [openrouter, setOpenrouter] = useState<OpenRouterResult | null>(null);
  useEffect(() => {
    let alive = true;
    getOpenRouterModels().then(r => alive && setOpenrouter(r));
    return () => {
      alive = false;
    };
  }, []);
  const countryOptions = Object.keys(countryNames).sort((a, b) => (modelCounts[b] ?? 0) - (modelCounts[a] ?? 0));
  return <>
      <PageHeader title="Models" lede="The minds behind the machine population. A hand-maintained table of the most significant models, each linked to its official announcement, with live layers from OpenRouter and Hugging Face below." data-id="element-1311" data-mp="1xdfnmfnctru4">
        <DataBadge mode="snapshot" source="Curated model table" asOf={COMPILED_LABEL} data-id="element-1312" data-mp="3q98hbftnijwo" />
        {openrouter && <DataBadge mode={openrouter.mode} source="OpenRouter" asOf={openrouter.asOf} data-id="element-1313" data-mp="3la5d1xlhwhpr" />}
      </PageHeader>

      <section className="mx-auto max-w-[1400px] px-5 md:px-8" data-id="element-1314" data-mp="3gasp19iw14ll">
        <dl className="grid grid-cols-3 border-b border-line" data-id="element-1315" data-mp="3gd551h5354w0">
          <div className="py-5 pr-4" data-id="element-1316" data-mp="3caikm1aqum8u">
            <dt className="text-[12.5px] text-muted" data-id="element-1317" data-mp="3cbjzkguvrsvu">Models listed</dt>
            <dd className="mt-1 font-mono text-2xl tabular text-ink" data-id="element-1318" data-mp="ct0upoha48k8">
              {total}
              <SourceRef id="censusCurated" data-id="element-1319" data-mp="7wk50puflm51" />
            </dd>
          </div>
          <div className="border-l border-line py-5 pl-4 md:pl-6" data-id="element-1320" data-mp="33qzhzr529rw">
            <dt className="text-[12.5px] text-muted" data-id="element-1321" data-mp="32682j04f6y9">Open-weight share</dt>
            <dd className="mt-1 font-mono text-2xl tabular text-ink" data-id="element-1322" data-mp="3v8304hrmc8gs">
              {openShare.toFixed(0)}%
              <SourceRef id="censusCurated" data-id="element-1323" data-mp="vmy06p8bc13u" />
            </dd>
          </div>
          <div className="border-l border-line py-5 pl-4 md:pl-6" data-id="element-1324" data-mp="qsk0iammb7td">
            <dt className="text-[12.5px] text-muted" data-id="element-1325" data-mp="29hmueyp2vb01">Newest release</dt>
            <dd className="mt-1 font-mono text-2xl tabular text-ink" data-id="element-1326" data-mp="2efy4najabnis">
              {formatReleased(newest)}
              <SourceRef id="censusCurated" data-id="element-1327" data-mp="2iht2g520birt" />
            </dd>
          </div>
        </dl>

        <aside className="mt-6 border-l-2 border-machine bg-machine/[0.05] px-4 py-3 text-[13.5px] text-ink/90" data-id="element-1328" data-mp="2ngmio58ohnvo">
          Frontier labs no longer disclose parameter counts — fields show “undisclosed” with source, not guesses.
        </aside>

        <div className="sticky top-[88px] z-20 -mx-5 mt-8 border-y border-line bg-bg/95 px-5 py-3 backdrop-blur md:-mx-8 md:px-8" data-id="element-1329" data-mp="2xdisqfe86i2c">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2" data-id="element-1330" data-mp="327n0h0vbeu4h">
            <ChipGroup label="Access" value={access} onChange={setAccess} options={ACCESS_OPTIONS} data-id="element-1331" data-mp="365u9tang8t5n" />
            <label className="flex items-center gap-2 text-[13px] text-muted" data-id="element-1332" data-mp="3b7rxqi6soc4x">
              Country
              <select value={country} onChange={e => setCountry(e.target.value)} className="rounded-md border border-line bg-surface px-2.5 py-1.5 text-[13px] text-ink focus:border-machine focus:outline-none" data-id="element-1333" data-mp="3g5te2vcu5xcp">
                <option value="all" data-id="element-1334" data-mp="2xj83xkodeibv">All countries</option>
                {countryOptions.map(c => <option key={c} value={c} data-id="element-1335" data-mp="3cagwa1jnqhsp">
                    {countryNames[c]}
                  </option>)}
              </select>
            </label>
          </div>
          <p className="mt-1.5 text-[11.5px] text-faint" data-id="element-1336" data-mp="2jk8l4tnjgxnm">{ACCESS_NOTES[access]}</p>
        </div>

        <div className="mt-8" data-id="element-1337" data-mp="2em6v1orp3y2n">
          <ModelMakers openrouter={openrouter} country={country} data-id="element-1338" data-mp="2thwk5vs6grbl" />
        </div>

        <div id="curated" className="mt-16" data-id="element-1339" data-mp="2tftj8gphlds3">
          <h2 className="mb-4 font-serif text-2xl text-ink md:text-3xl" data-id="element-1340" data-mp="2oia47ekyswz3">Significant models</h2>
          <CuratedModelTable access={access} country={country} data-id="element-1341" data-mp="8r5sp9ut8az2" />
        </div>

        <div className="mt-16" data-id="element-1342" data-mp="3sks39bnbpa3">
          <OpenRouterTable result={openrouter} access={access} country={country} data-id="element-1343" data-mp="3tdu5wzntqlw" />
        </div>

        <div className="mt-16" data-id="element-1344" data-mp="bbu0az8f1hxk">
          <HFLiveTable data-id="element-1345" data-mp="basebzpv3b12" />
        </div>
      </section>
    </>;
}